import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

/**
 * Observes an element with a `ResizeObserver` and reports a derived boolean
 * (computed from the element's size by `predicate`).
 *
 * Crucially, state only updates when the boolean result *flips* — never on
 * every resize frame. A consumer that re-renders an expensive subtree (e.g. a
 * chart) therefore does NOT re-render continuously while the element is being
 * drag-resized; it re-renders at most once, when the value crosses the
 * predicate's threshold. (Returning the raw size instead would re-render the
 * consumer on every frame and make resizing janky — see RSP-4068.)
 *
 * The predicate is read through a ref so passing a fresh inline function each
 * render does not re-subscribe the observer. Where `ResizeObserver` is
 * unavailable (e.g. happy-dom in unit tests) the hook safely no-ops and the
 * value stays at its initial `false`.
 *
 * @returns a tuple of `[ref, value]` — attach `ref` to the element to observe.
 */
export const useElementCompact = <T extends HTMLElement = HTMLDivElement>(
  predicate: (size: { width: number; height: number }) => boolean
): [RefObject<T | null>, boolean] => {
  const ref = useRef<T>(null);
  const predicateRef = useRef(predicate);
  predicateRef.current = predicate;
  const [value, setValue] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }

    let rafId: ReturnType<typeof requestAnimationFrame> | null = null;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      // `contentRect` is delivered by the observer — no layout read needed.
      const { width, height } = entry.contentRect;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        const next = predicateRef.current({ width, height });
        // Only re-render when the derived value actually flips.
        setValue((prev) => (prev === next ? prev : next));
      });
    });
    observer.observe(el);

    return () => {
      observer.disconnect();
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  return [ref, value];
};
